import React from 'react'
import { BottomSheet } from '@s4l/ds'
import cn from 'classnames'
import IconMobilePhone from 'client/icons/MobilePhone'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import { useDevice } from 'src/hooks/useDevice'
import { Devices } from 'src/types/device'
import BestPropertyTag from '../../BestPropertyTag'
import InfoButton from '../../InfoButton'
import BoardDescription from '../Board/BoardDescription'
import BoardIcon from '../Board/BoardIcon'
import BoardName from '../Board/BoardName'
import RateNOR, { RateNORCancellationDescription, RateNORPaymentDescription } from './RateNOR'
import RateNRF, { RateNRFCancellationDescription, RateNRFPaymentDescription } from './RateNRF'
import RateSFL, { RateSFLCancellationDescription, RateSFLPaymentDescription } from './RateSFL'
import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'

import styles from './styles.module.css'

export enum Rate {
  NOR = 'NOR',
  SFL = 'SFL',
  NRF = 'NRF',
}

export const ROOM_ONLY_CODE = 'RO'

export interface Cancellation {
  amount: number
  from: string
  currency: string
}
interface OfferConditionsProps {
  code: string
  name: string
  cancellation: Cancellation[]
  currency: string
  isMobile?: boolean
  paymentPolicy: Rate
  payRate: number
  bestPrice?: boolean
}

const rates = {
  [Rate.NOR]: RateNOR,
  [Rate.NRF]: RateNRF,
  [Rate.SFL]: RateSFL,
}

const ratesPaymentDescription = {
  [Rate.NOR]: RateNORPaymentDescription,
  [Rate.NRF]: RateNRFPaymentDescription,
  [Rate.SFL]: RateSFLPaymentDescription,
}

const ratesCancellationDescription = {
  [Rate.NOR]: RateNORCancellationDescription,
  [Rate.NRF]: RateNRFCancellationDescription,
  [Rate.SFL]: RateSFLCancellationDescription,
}

const getBestPropertyTag = (paymentPolicy: Rate, bestPrice: boolean, intl: IntlShape) => {
  return paymentPolicy === Rate.SFL ? (
    <BestPropertyTag branded copy={intl.formatMessage({ id: 'policies.best-deal' })} />
  ) : (
    bestPrice && <BestPropertyTag copy={intl.formatMessage({ id: 'global.best-price' })} />
  )
}

const OfferConditions = ({
  code,
  currency,
  name = '',
  cancellation,
  isMobile = false,
  paymentPolicy,
  payRate,
  bestPrice = false,
}: OfferConditionsProps) => {
  const intl = useIntl()
  const { device } = useDevice()

  const RateComponent = rates[paymentPolicy]
  const RatePaymentComponent = ratesPaymentDescription[paymentPolicy]
  const RateCancellationComponent = ratesCancellationDescription[paymentPolicy]

  const rateProps = {
    cancellation: cancellation[0],
    currency,
    payRate,
  }

  const showMobileOnlyPrice = isMobile && device !== Devices.Desktop

  return (
    <>
      <div className={styles.offerConditionsContainer}>
        <ul className={styles.offerConditionsList}>
          <li className={cn(styles.textWithIcon, { [styles.greenText]: code !== ROOM_ONLY_CODE })}>
            <BottomSheet>
              <BottomSheet.Trigger className={styles.boardInfoButton}>
                <BoardIcon code={code} />
                <BoardName code={code} name={name} />
                <InfoButton />
              </BottomSheet.Trigger>
              <BottomSheet.Content
                title={intl.formatMessage({ id: 'policies.rate-conditions' })}
                size="s"
                modalOnDesktop
              >
                <div className={styles.detailContainer}>
                  {code !== ROOM_ONLY_CODE ? (
                    <>
                      <h5 className={styles.detailTitle}>
                        <BoardName code={code} name="" />
                      </h5>
                      <p>
                        <BoardDescription code={code} />
                      </p>
                    </>
                  ) : null}
                  <h5 className={styles.detailTitle}>
                    <>{intl.formatMessage({ id: 'global.payment' })}</>
                  </h5>
                  <RatePaymentComponent {...rateProps} />
                  <h5 className={styles.detailTitle}>
                    <>{intl.formatMessage({ id: 'global.cancellation-policies' })}</>
                  </h5>
                  <RateCancellationComponent {...rateProps} />
                </div>
              </BottomSheet.Content>
            </BottomSheet>
            {getBestPropertyTag(paymentPolicy, bestPrice, intl)}
          </li>

          {showMobileOnlyPrice && (
            <li>
              <IconMobilePhone size={16} />
              {intl.formatMessage({ id: 'global.mobile-only-price' })}
            </li>
          )}

          <RateComponent {...rateProps} />
        </ul>
      </div>
    </>
  )
}

OfferConditions.displayName = 'OfferConditions'
export default withErrorBoundary(OfferConditions)
